import AppDialog from '../../../../../components/common/AppDialog';
import { SVG } from '../../../../../constants/assetLists';
import AppSwitch from '../../../../../components/common/AppSwitch';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import AppButton from '../../../../../components/common/AppButton';
import AppTextInput from '../../../../../components/common/AppTextInput';
import { useSnackbar } from '../../../../../context/SnackbarContextProvider';
import MUIIconCheckboxSelect from '../../../../../components/common/MUIIconCheckboxSelect';
import ChecklistBox from './components/ChecklistBox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  handleCreateChecklistService,
  handleGetChecklistService,
  handleUpdateChecklistService,
} from '../../../../../services/operations/checklist';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import { handleGetAllDepartmentsService } from '../../../../../services/operations/departments';
import { getCorrectPath } from '../../../../../utils';
import { useOperationsContext } from '../../../../../context/OperationsContextProvider';
import AppMUIAutocomplete from '../../../../../components/common/AppMUIAutoComplete';
import { Checkbox, Chip } from '@mui/material';
import {
  useClientsContext,
  getLocationFilterParams,
  SubBranchData,
} from '../../../../../context/clients/ClientsContextProvider';
import BranchTreeAutocomplete from '../../../../../components/common/BranchTreeAutocomplete';
import { ICONS } from '../../../../users/data';
import { handleGetShiftsService } from '../../../../../services/shifts';
import { TIMES } from '../AddAnnouncement/components/Time';
import { DateInput } from '../CustomTiming/components/DateInput';
import moment from 'moment';

type Props = {
  checklistId?: string;
  open: boolean;
  onClose: () => void;
  useLocationContext?: boolean;
  copyMode?: boolean;
};

export type DepartmentOptionType = {
  label: string;
  value: string;
  icon: string;
  groupLabel?: string;
};

type ShiftOptionType = {
  label: string;
  value: string;
  groupLabel?: string;
};

type FormValues = {
  clientId: string;
  branchIds: string[];
  subBranchIds: string[];
  status: boolean;
  departments: string[];
  shiftTime: string[];
  title: string;
  description?: string;
  dueNotificationShiftOffset?: string;
  dueNotificationTime?: string;
  dueNotificationDate?: string;
  showInBrief: boolean;
  approvalRequired: boolean;
  enableComments: boolean;
  resetDaily: boolean;
  checked?: boolean;
  list: { instruction?: string; status: boolean }[];
};

const SWITCHES: {
  label: string;
  name: 'showInBrief' | 'approvalRequired' | 'enableComments' | 'resetDaily';
}[] = [
  { label: 'אפשור הוספת תגובות', name: 'enableComments' },
  { label: 'חובת אישור', name: 'approvalRequired' },
  { label: 'הצגה בבריף', name: 'showInBrief' },
  { label: 'איפוס יומי', name: 'resetDaily' },
];

const DUE_NOTIFICATION_SHIFT_OFFSET_OPTIONS = [
  { label: 'שעה לפני תחילת משמרת', value: '1_hour_before_start' },
  { label: 'חצי שעה לפני תחילת משמרת', value: '30_min_before_start' },
  { label: 'חצי שעה לפני סוף משמרת', value: '30_min_before_end' },
  { label: 'שעה לפני סוף משמרת', value: '1_hour_before_end' },
];

const HHMM_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

const zodSchema = z
  .object({
    clientId: z.string(),
    branchIds: z.array(z.string()),
    subBranchIds: z.array(z.string()),
    status: z.boolean(),
    departments: z
      .array(z.string().min(1, 'מזהה מחלקה לא תקין'))
      .min(1, 'יש לבחור לפחות מחלקה אחת'),
    shiftTime: z.array(z.string()),
    title: z.string({ error: 'שדה חובה!' }).min(3, 'שדה חובה!'),
    description: z.string().optional(),
    dueNotificationShiftOffset: z.string().optional(),
    dueNotificationTime: z.string().optional(),
    dueNotificationDate: z.string().optional(),
    showInBrief: z.boolean(),
    approvalRequired: z.boolean(),
    enableComments: z.boolean(),
    resetDaily: z.boolean(),
    list: z
      .array(z.any())
      .min(1, { message: 'שדה חובה!' })
      .refine(
        (items) => items.some((it) => (it.instruction ?? '').trim().length > 0),
        { message: 'לפחות פריט אחד חייב לכלול הוראה לא ריקה' },
      ),
  })
  .superRefine((data, ctx) => {
    if (
      data.approvalRequired &&
      data.shiftTime.length > 0 &&
      !data.dueNotificationShiftOffset?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dueNotificationShiftOffset'],
        message: 'שדה חובה!',
      });
    }

    if (
      data.approvalRequired &&
      data.shiftTime.length === 0 &&
      !data.dueNotificationTime?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dueNotificationTime'],
        message: 'שדה חובה!',
      });
    }

    if (
      data.approvalRequired &&
      data.shiftTime.length === 0 &&
      data.dueNotificationTime?.trim() &&
      !HHMM_REGEX.test(data.dueNotificationTime)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dueNotificationTime'],
        message: 'יש להזין שעה בפורמט HH:MM',
      });
    }
  });

function AddEditCheckList({
  checklistId,
  open,
  onClose,
  useLocationContext = false,
  copyMode = false,
}: Props) {
  const { showSnackbar } = useSnackbar();

  const { selectedShift, setNewChecklistId, openAddEditAnnouncementDialog } =
    useOperationsContext();

  const {
    selectedLocation,
    allClients,
    selectedClient,
    setRecentlyCopiedLocationIds,
  } = useClientsContext();

  // Track the index of the newly added item for auto-focus
  const [newlyAddedIndex, setNewlyAddedIndex] = useState<number | null>(null);

  const divRef = useRef<HTMLDivElement | null>(null);
  const copyTargetLocationsRef = useRef<string[]>([]);
  const departmentsInitializedRef = useRef(false);
  const hydratedRef = useRef(false);
  const shiftTimeInitializedRef = useRef(false);
  const queryClient = useQueryClient();

  const { data: checklistData } = useQuery({
    queryKey: [QUERY_KEYS.GET_CHECKLIST, checklistId],
    queryFn: () => handleGetChecklistService(checklistId!),
    staleTime: 0,
    enabled: !!checklistId && open,
  });

  const {
    formState: { errors, isSubmitting, isValid },
    reset,
    control,
    setValue,
    getValues,
    watch,
    handleSubmit,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      clientId: '',
      branchIds: [],
      subBranchIds: [],
      status: true,
      departments: [],
      shiftTime: [],
      title: '',
      description: '',
      dueNotificationShiftOffset: '',
      dueNotificationTime: '',
      dueNotificationDate: '',
      showInBrief: false,
      approvalRequired: false,
      enableComments: false,
      resetDaily: true,
      list: [],
    },
    resolver: zodResolver(zodSchema),
  });

  const {
    fields: listFields,
    append: listAppend,
    remove: listRemove,
  } = useFieldArray<FormValues, 'list', 'id'>({
    control,
    name: 'list',
  });

  const departments = watch('departments');
  const shiftTime = watch('shiftTime');
  const formClientId = watch('clientId');
  const formBranchIds = watch('branchIds');
  const formSubBranchIds = watch('subBranchIds');

  const isClientSelected = selectedLocation?.isClient;
  const isBranchSelected = selectedLocation?.isBranch;
  const isSubBranchSelected = selectedLocation?.isSubBranch;

  const departmentClientId = formClientId?.length
    ? formClientId
    : useLocationContext
      ? isClientSelected
        ? selectedLocation.id
        : selectedLocation?.clientId || selectedClient?._id
      : formClientId || selectedClient?._id;

  const departmentBranchId: string[] | undefined = isClientSelected
    ? formBranchIds
    : isBranchSelected && formBranchIds?.length
      ? formBranchIds
      : isSubBranchSelected && selectedLocation?.branchId
        ? [selectedLocation.branchId]
        : [];

  const departmentSubBranchId: string[] | undefined =
    isClientSelected || isBranchSelected
      ? formSubBranchIds
      : isSubBranchSelected && selectedLocation?.id
        ? [selectedLocation.id]
        : [];
  const departmentBranchKey = (departmentBranchId ?? []).join('|');
  const departmentSubBranchKey = (departmentSubBranchId ?? []).join('|');

  const { data: allDepartments } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_DEPARTMENTS,
      departmentClientId,
      departmentBranchId,
      departmentSubBranchId,
    ],
    queryFn: () =>
      handleGetAllDepartmentsService({
        clientId: departmentClientId,
        ...(departmentBranchId && { branchId: departmentBranchId }),
        ...(departmentSubBranchId && { subBranchId: departmentSubBranchId }),
      }),
    enabled: open && !!departmentClientId,
  });

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (allClients ?? []).forEach((client: any) => {
      map.set(client.id, client.name);
      (client.branches ?? []).forEach((branch: any) => {
        map.set(branch.id, branch.name);
        (branch.subBranches ?? []).forEach((subBranch: any) => {
          map.set(subBranch.id, subBranch.name);
        });
      });
    });
    return map;
  }, [allClients]);

  const resolveLocationId = (
    entries: any[] | undefined,
    preferredIds: string[],
  ) => {
    const ids =
      entries
        ?.map((entry) => (typeof entry === 'string' ? entry : entry?._id))
        .filter(Boolean) ?? [];
    return (
      ids.find((id) => preferredIds.includes(id)) ??
      ids.find((id) => locationNameById.has(id))
    );
  };

  const resolveLocationName = (
    entries: any[] | undefined,
    preferredIds: string[],
  ) => {
    const entry =
      entries?.find((item) => {
        const id = typeof item === 'string' ? item : item?._id || item?.id;
        return id && preferredIds.includes(id);
      }) ??
      entries?.find((item) => {
        const id = typeof item === 'string' ? item : item?._id || item?.id;
        return id && locationNameById.has(id);
      });

    if (!entry) return '';

    if (typeof entry !== 'string' && entry.name) return entry.name;

    return locationNameById.get(entry) ?? '';
  };

  const getDeptGroupLabel = (department: any) => {
    const subBranchId = resolveLocationId(
      department.subBranchId,
      departmentSubBranchId ?? [],
    );
    if (subBranchId) return locationNameById.get(subBranchId) ?? '';

    const branchId = resolveLocationId(
      department.branchId,
      departmentBranchId ?? [],
    );
    if (branchId) return locationNameById.get(branchId) ?? '';

    const clientId = resolveLocationId(
      department.clientId,
      departmentClientId ? [departmentClientId] : [],
    );
    return clientId ? (locationNameById.get(clientId) ?? '') : '';
  };

  const getShiftGroupLabel = (shift: any) => {
    const subBranchName = resolveLocationName(
      shift.subBranchIds,
      departmentSubBranchId ?? [],
    );
    if (subBranchName) return subBranchName;

    const branchName = resolveLocationName(
      shift.branchIds,
      departmentBranchId ?? [],
    );
    if (branchName) return branchName;

    return resolveLocationName(
      shift.clientIds,
      departmentClientId ? [departmentClientId] : [],
    );
  };

  const { data: shifts } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_ALL_SHIFTS,
      'MODAL',
      departmentClientId,
      departmentBranchId,
      departmentSubBranchId,
    ],
    queryFn: () =>
      handleGetShiftsService({
        ...(departmentClientId && { clientId: departmentClientId }),
        ...(departmentBranchId && { branchId: departmentBranchId }),
        ...(departmentSubBranchId && { subBranchId: departmentSubBranchId }),
      }),
    enabled: open && !!departmentClientId,
  });
  const shiftTimeList = shifts?.data;
  const formattedShifts = useMemo<ShiftOptionType[]>(() => {
    if (!shiftTimeList?.shifts?.length) return [];

    const sorted =
      [...shiftTimeList.shifts]
        .map(
          (item: {
            timing: { startTime: string; endTime: string };
            name: string;
            _id: string;
            clientIds?: any[];
            branchIds?: any[];
            subBranchIds?: any[];
          }) => ({
            label: `${item.name} (${item.timing.startTime}-${item.timing.endTime})`,
            value: item._id,
            groupLabel: getShiftGroupLabel(item),
            startTime: item.timing.startTime,
            endTime: item.timing.endTime,
          }),
        )
        ?.sort((a, b) => {
          const group = a.groupLabel.localeCompare(b.groupLabel);
          if (group !== 0) return group;
          const startA = a.startTime.localeCompare(b.startTime);
          if (startA !== 0) return startA;
          return a.endTime.localeCompare(b.endTime);
        })
        .map(({ label, value, groupLabel }) => ({ label, value, groupLabel }));
    return [{ label: 'כל המשמרות', value: 'all', groupLabel: '' }, ...sorted];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shiftTimeList,
    locationNameById,
    departmentClientId,
    departmentBranchKey,
    departmentSubBranchKey,
  ]);


  useEffect(() => {
    departmentsInitializedRef.current = false;
    hydratedRef.current = false;
    shiftTimeInitializedRef.current = false;
  }, [open, checklistId]);

  useEffect(() => {
    if (!open || checklistId || copyMode || !useLocationContext) return;

    if (selectedLocation?.isBranch) {
      setValue('branchIds', [selectedLocation.id]);
      setValue('subBranchIds', []);
    } else if (selectedLocation?.isSubBranch) {
      setValue('branchIds', [selectedLocation.branchId]);
      setValue('subBranchIds', [selectedLocation.id]);
    }
  }, [open, checklistId, copyMode, useLocationContext, selectedLocation, setValue]);

  useEffect(() => {
    if (!checklistId || !checklistData || !open) return;
    if (hydratedRef.current) return;

    hydratedRef.current = true;

    const titleValue = copyMode
      ? (checklistData?.title || '') + ' - עותק'
      : checklistData?.title || '';
    const dueNotification = checklistData?.dueNotification;
    setValue('title', titleValue);
    setValue('description', checklistData?.description || '');
    setValue(
      'dueNotificationShiftOffset',
      dueNotification?.mode === 'shift' ? dueNotification?.shiftOffset || '' : '',
    );
    setValue(
      'dueNotificationTime',
      dueNotification?.mode === 'datetime' ? dueNotification?.time || '' : '',
    );
    setValue(
      'dueNotificationDate',
      dueNotification?.mode === 'datetime' && dueNotification?.date
        ? dueNotification.date
        : '',
    );
    setValue('showInBrief', checklistData?.showInBrief || false);
    setValue('approvalRequired', checklistData?.approvalRequired || false);
    setValue('enableComments', checklistData?.commentsEnabled || false);
    setValue('resetDaily', checklistData?.resetDaily || false);

    let locationStatus: string | undefined;
    if (useLocationContext && selectedLocation) {
      if (selectedLocation.isClient) {
        const clientEntry = checklistData?.clientIds?.find(
          (c: any) => c._id === selectedLocation.id,
        );
        locationStatus = clientEntry?.status;
      } else if (selectedLocation.isSubBranch) {
        const subBranchEntry = checklistData?.subBranchIds?.find(
          (b: any) => b._id === selectedLocation.id,
        );
        locationStatus = subBranchEntry?.status;
      } else {
        const branchEntry = checklistData?.branchIds?.find(
          (b: any) => b._id === selectedLocation.id,
        );
        locationStatus = branchEntry?.status;
      }
    } else if (!useLocationContext && selectedLocation) {
      if (selectedLocation.isClient) {
        const clientEntry = checklistData?.clientIds?.find(
          (c: any) => c._id === selectedLocation.id,
        );
        locationStatus = clientEntry?.status;
      } else if (selectedLocation.isSubBranch) {
        const subBranchEntry = checklistData?.subBranchIds?.find(
          (b: any) => b._id === selectedLocation.id,
        );
        locationStatus = subBranchEntry?.status;
      } else {
        const branchEntry = checklistData?.branchIds?.find(
          (b: any) => b._id === selectedLocation.id,
        );
        locationStatus = branchEntry?.status;
      }
    } else if (!useLocationContext && selectedClient) {
      const clientEntry = checklistData?.clientIds?.find(
        (c: any) => c._id === selectedClient._id,
      );
      locationStatus = clientEntry?.status;
    }
    setValue('status', locationStatus === 'active');

    const existingClientId = checklistData?.clientIds?.[0]?._id || '';
    setValue('clientId', existingClientId);

    setValue(
      'branchIds',
      checklistData?.branchIds?.map((b: any) => b?._id) || [],
    );
    setValue(
      'subBranchIds',
      checklistData?.subBranchIds?.map((b: any) => b?._id) || [],
    );

    setValue(
      'list',
      checklistData?.list?.map((item: any) => ({
        ...item,
        instruction: item?.details || '',
        status: item?.activeStatus === 'active',
      })) || [],
    );
  }, [
    checklistId,
    checklistData,
    open,
    setValue,
    copyMode,
    selectedLocation,
    useLocationContext,
    selectedClient,
  ]);

  useEffect(() => {
    if (!checklistId || !checklistData || !open) return;
    if (shiftTimeInitializedRef.current) return;
    if (!shiftTimeList?.shifts?.length) return;

    shiftTimeInitializedRef.current = true;

    setValue(
      'shiftTime',
      checklistData?.shiftTime?.length === shiftTimeList.shifts.length
        ? ['all']
        : checklistData?.shiftTime?.map((d: any) => d._id) || [],
    );
  }, [checklistId, checklistData, open, shiftTimeList, setValue]);

  useEffect(() => {
    if (
      !checklistId ||
      !checklistData ||
      !open ||
      !allDepartments?.departments?.length
    )
      return;
    if (departmentsInitializedRef.current) return;

    departmentsInitializedRef.current = true;

    if (checklistData?.departments === 'all') {
      setValue('departments', ['all']);
    } else if (Array.isArray(checklistData?.departments)) {
      setValue(
        'departments',
        checklistData?.departments?.length ===
          allDepartments?.departments?.length
          ? ['all']
          : checklistData?.departments?.map((d: any) => d?._id) || [],
      );
    } else {
      setValue('departments', []);
    }
  }, [checklistId, checklistData, open, allDepartments, setValue]);

  const departmentOptions = useMemo<DepartmentOptionType[]>(() => {
    if (!allDepartments?.departments?.length) return [];

    const dept = allDepartments.departments
      .map((department: any) => ({
        label: department.name,
        value: department._id,
        icon: department.icon.startsWith('icon')
          ? ICONS.find((icon) => icon.id === department.icon)?.icon || ''
          : getCorrectPath(department.icon, 'image'),
        groupLabel: getDeptGroupLabel(department),
      }))
      .sort((a: { groupLabel: string }, b: { groupLabel: string }) =>
        a.groupLabel.localeCompare(b.groupLabel),
      );

    return [
      {
        label: 'כל המחלקות',
        value: 'all',
        icon: SVG.saveAllIcon,
        groupLabel: '',
      },
      ...dept,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allDepartments,
    locationNameById,
    departmentClientId,
    departmentBranchKey,
    departmentSubBranchKey,
  ]);

  // -----------------------------
  // Derived "value" for UI (no local state)
  // -----------------------------
  const departmentsValue = useMemo(() => {
    if (!departmentOptions.length) return [];
    const selected = departments ?? [];
    const selectedIds =
      selected?.[0] === 'all'
        ? departmentOptions.map((o) => o.value)
        : selected;

    return departmentOptions.filter((o) => selectedIds.includes(o.value));
  }, [departments, departmentOptions]);

  const shiftTimeValue = useMemo(() => {
    if (!formattedShifts.length) return [];
    const selected = shiftTime ?? [];
    const selectedIds =
      selected?.[0] === 'all' ? formattedShifts.map((o) => o.value) : selected;

    return formattedShifts.filter((o) => selectedIds.includes(o.value));
  }, [shiftTime, formattedShifts]);

  // Determine which client to use for fetching branches
  // In edit mode, use the stored clientId from form
  // In create mode with location context, use the selected location
  // In create mode without location context, use selectedClient from context
  let clientIdForBranches: string | undefined;
  if (checklistId) {
    clientIdForBranches = watch('clientId');
  } else if (useLocationContext) {
    clientIdForBranches = selectedLocation?.isClient
      ? selectedLocation.id
      : selectedLocation?.clientId || selectedClient?._id;
  } else {
    clientIdForBranches = selectedClient?._id;
  }

  const clientForBranches = allClients?.find(
    (c) => c.id === clientIdForBranches,
  );
  const clientBranches = clientForBranches?.branches ?? [];

  const selectedBranchHasSubBranches = useMemo(() => {
    if (
      !selectedLocation?.isBranch ||
      !selectedLocation.id ||
      !clientForBranches
    ) {
      return false;
    }
    const branch = clientForBranches.branches?.find(
      (b) => b.id === selectedLocation.id,
    );
    return (branch?.subBranches?.length ?? 0) > 0;
  }, [clientForBranches, selectedLocation]);

  const displayBranchTree = copyMode
    ? true
    : // !copyMode &&
      clientBranches.length > 0 &&
      (!useLocationContext ||
        selectedLocation?.isClient ||
        (selectedLocation?.isBranch && selectedBranchHasSubBranches));

  // -----------------------------
  // Handlers (same logic)
  // -----------------------------
  const handleDepartmentsChange = (
    e: React.SyntheticEvent,
    value: { value: string }[] | null,
  ) => {
    const text = e.currentTarget.textContent;
    const isAllSelectedByText = text === 'כל המחלקות';
    const currentDepartments = getValues().departments || [];

    const selectedValues = value?.map((item) => item.value) || [];
    const allDeptSelected = allDepartments?.departments?.every((dept: any) =>
      selectedValues.includes(dept._id),
    );
    const hasAllInSelection = selectedValues.includes('all');
    const hadAllPreviously = currentDepartments.includes('all');

    let newValue: string[] = [];
    if (hadAllPreviously && !hasAllInSelection) {
      newValue = []; // reset
    } else if (hadAllPreviously) {
      newValue = selectedValues.filter((v) => v !== 'all');
    } else if (isAllSelectedByText || hasAllInSelection || allDeptSelected) {
      newValue = ['all'];
    } else {
      newValue = selectedValues;
    }

    setValue('departments', newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleShiftTimesChange = (
    e: React.SyntheticEvent,
    value: { value: string }[] | null,
  ) => {
    const text = e.currentTarget.textContent;

    // BUGFIX: this must match "All shifts" label, not "All departments"
    const isAllSelectedByText = text === 'כל המשמרות';

    const currentShiftTimes = getValues().shiftTime || [];

    const selectedValues = value?.map((item) => item.value) || [];
    const allShiftSelected = shiftTimeList?.shifts?.every((shift: any) =>
      selectedValues.includes(shift._id),
    );
    const hasAllInSelection = selectedValues.includes('all');
    const hadAllPreviously = currentShiftTimes.includes('all');

    let newValue: string[] = [];
    if (hadAllPreviously && !hasAllInSelection) {
      newValue = []; // reset
    } else if (hadAllPreviously) {
      newValue = selectedValues.filter((v) => v !== 'all');
    } else if (isAllSelectedByText || hasAllInSelection || allShiftSelected) {
      newValue = ['all'];
    } else {
      newValue = selectedValues;
    }

    setValue('shiftTime', newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const addToCheckList = async () => {
    const currentLength = getValues().list?.length || 0;
    listAppend({ instruction: '', status: true });

    // Set the newly added index for auto-focus
    setNewlyAddedIndex(currentLength);

    requestAnimationFrame(() => {
      if (divRef.current) {
        divRef.current.scrollTop = divRef.current.scrollHeight;
      }
    });
  };

  const handleModalClose = () => {
    reset();
    departmentsInitializedRef.current = false;
    hydratedRef.current = false;
    shiftTimeInitializedRef.current = false;
    setNewlyAddedIndex(null);
    onClose();
  };

  const onSubmit = (formData: FormValues) => {
    let clientId: string | undefined;
    let branchIds: string[];
    let subBranchIds: string[];

    if (copyMode) {
      clientId = formData.clientId;
      branchIds = formData.branchIds;
      subBranchIds = formData.subBranchIds;
    } else if (checklistId) {
      clientId = formData.clientId;
      branchIds = formData.branchIds;
      subBranchIds = formData.subBranchIds;
    } else if (useLocationContext) {
      clientId = isClientSelected
        ? selectedLocation?.id
        : selectedLocation?.clientId;

      if (isSubBranchSelected) {
        branchIds = selectedLocation?.branchId
          ? [selectedLocation.branchId]
          : [];
        subBranchIds = selectedLocation?.id ? [selectedLocation.id] : [];
      } else {
        branchIds = formData.branchIds;
        subBranchIds = formData.subBranchIds;
      }
    } else if (selectedLocation?.isSubBranch) {
      clientId = selectedLocation.clientId;
      branchIds = [selectedLocation.branchId];
      subBranchIds = [selectedLocation.id];
    } else if (selectedLocation && !selectedLocation.isClient) {
      clientId = selectedLocation.clientId;
      branchIds = [selectedLocation.id];
      subBranchIds = formData.subBranchIds;
    } else {
      clientId = selectedClient?._id;
      branchIds = formData.branchIds;
      subBranchIds = formData.subBranchIds;
    }

    // Determine the status value for the location
    const statusValue = formData.status ? 'active' : 'inactive';

    const isEditMode = !!checklistId && !copyMode;

    // Determine locationId and locationType for targeted status updates
    let locationId: string | undefined;
    let locationType: 'client' | 'branch' | 'subBranch' | undefined;
    if (isEditMode) {
      if (useLocationContext && selectedLocation) {
        locationId = selectedLocation.id;
        locationType = selectedLocation.isClient
          ? 'client'
          : selectedLocation.isSubBranch
            ? 'subBranch'
            : 'branch';
      } else if (!useLocationContext && selectedClient) {
        locationId = selectedClient._id;
        locationType = 'client';
      }
    }

    // In edit mode, only update the status of the selected location
    // For other locations, preserve their existing status from checklistData
    // In create/copy mode, all locations get the same initial status
    const clientIdsPayload = clientId
      ? [
          {
            _id: clientId,
            status: isEditMode
              ? locationType === 'client' && clientId === locationId
                ? statusValue // Update selected client's status
                : checklistData?.clientIds?.find((c: any) => c._id === clientId)
                    ?.status || 'active' // Preserve existing status
              : statusValue, // Create/copy mode: use new status
          },
        ]
      : [];

    const branchIdsPayload = branchIds.map((branchId) => ({
      _id: branchId,
      status: isEditMode
        ? locationType === 'branch' && branchId === locationId
          ? statusValue
          : checklistData?.branchIds?.find((b: any) => b._id === branchId)
              ?.status || 'active'
        : statusValue,
    }));

    const subBranchIdsPayload = subBranchIds.map((subBranchId) => ({
      _id: subBranchId,
      status: isEditMode
        ? locationType === 'subBranch' && subBranchId === locationId
          ? statusValue
          : checklistData?.subBranchIds?.find((b: any) => b._id === subBranchId)
              ?.status || 'active'
        : statusValue,
    }));

    const resolvedShiftTime = formData.shiftTime?.includes('all')
      ? formattedShifts
          .filter((item) => item.value !== 'all')
          .map((item) => item.value)
      : formData.shiftTime;

    const dueNotification = !formData.approvalRequired
      ? isEditMode
        ? null
        : undefined
      : resolvedShiftTime.length > 0
        ? {
            mode: 'shift',
            shiftOffset: formData.dueNotificationShiftOffset,
          }
        : {
            mode: 'datetime',
            time: formData.dueNotificationTime,
            ...(formData.dueNotificationDate && {
              date: formData.dueNotificationDate,
            }),
          };

    const payload = {
      clientIds: clientIdsPayload,
      branchIds: branchIdsPayload,
      subBranchIds: subBranchIdsPayload,
      // Include locationId and locationType for targeted status updates in edit mode
      ...(isEditMode &&
        locationId &&
        locationType && { locationId, locationType }),
      departments: formData.departments?.includes('all')
        ? 'all'
        : formData.departments,
      shiftTime: resolvedShiftTime,
      title: formData.title,
      description: formData.description,
      ...(dueNotification !== undefined && { dueNotification }),
      showInBrief: formData.showInBrief,
      approvalRequired: formData.approvalRequired,
      commentsEnabled: formData.enableComments,
      resetDaily: formData.resetDaily,
      list: formData.list
        ?.filter((item) => item.instruction?.trim())
        ?.map((item: any) => ({
          ...item,
          details: item.instruction,
          activeStatus: item.status ? 'active' : 'inactive',
          status: formData.approvalRequired ? 'not_approved' : 'approved',
          comments:
            item?.comments?.map((comment: any) => ({
              ...comment,
              senderId: comment?.senderId?._id || comment?.senderId || '',
            })) ?? [],
        })),
    };

    if (copyMode) {
      // Store target locations for onCopySuccess callback
      const targetLocationIds = [
        ...(clientId ? [clientId] : []),
        ...branchIds,
        ...subBranchIds,
      ];
      copyTargetLocationsRef.current = targetLocationIds;
      // Copy mode: always create new checklist
      handleCreateChecklist(payload);
    } else if (checklistId) {
      handleUpdateChecklist({ id: checklistId, payload });
    } else {
      handleCreateChecklist(payload);
    }
  };

  const { mutate: handleCreateChecklist, isPending } = useMutation({
    mutationFn: handleCreateChecklistService,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS_SHIFT_ID, selectedShift],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GROUPED_CHECKLISTS],
      });
      if (openAddEditAnnouncementDialog) {
        setNewChecklistId(data?.data?._id || null);
      }
      // Set recently copied location IDs for sidebar indicator
      if (
        copyMode &&
        useLocationContext &&
        copyTargetLocationsRef.current.length > 0
      ) {
        setRecentlyCopiedLocationIds(copyTargetLocationsRef.current);
        copyTargetLocationsRef.current = [];
      }
      showSnackbar({
        message: copyMode
          ? "צ'קליסט הועתק בהצלחה"
          : (data?.message ?? `צ'קליסט נוצרה בהצלחה ונשמרה במאגר הצ'קליסטים`),
        type: 'success',
      });
      handleModalClose();
    },
  });

  const { mutate: handleUpdateChecklist, isPending: isUpdatePending } =
    useMutation({
      mutationFn: (data: Record<string, any>) =>
        handleUpdateChecklistService(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS_SHIFT_ID],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_GROUPED_CHECKLISTS],
        });

        showSnackbar({
          message: data?.message ?? 'הרשימה עודכנה בהצלחה',
          type: 'success',
        });
        handleModalClose();
      },
    });

  const checklistHeaderText = useMemo(() => {
    const list = getValues().list || [];

    if (list.length === 0) return '0 ברשימה';

    const active = list.filter((item) => item.status).length;
    const inactive = list.length - active;

    const activeText = active === 1 ? 'פעיל' : 'פעילים';
    const inactiveText = inactive === 1 ? 'כבוי' : 'כבויים';

    if (list.length === 1) {
      return active === 1 ? `1 ${activeText}` : `1 ${inactiveText}`;
    }

    if (active === 0) return `${inactive} ${inactiveText}`;
    if (inactive === 0) return `${active} ${activeText}`;

    return `${active} ${activeText} | ${inactive} ${inactiveText}`;
  }, [watch()]);

  return (
    <AppDialog
      showModel={open}
      onClose={handleModalClose}
      headerTopContent={
        <div className="flex justify-end">
          <div className="flex items-center gap-2">
            <Controller
              name="status"
              control={control}
              render={({ field: { onChange, value } }) => (
                <AppSwitch onChange={onChange} checked={value} />
              )}
            />
            <p
              className={`m-0 text-black ${watch('status') ? 'font-medium' : ''}`}
            >
              צ'קליסט פעיל
            </p>
          </div>
        </div>
      }
      titleField={
        <Controller
          name="title"
          control={control}
          render={({ field: { value } }) => (
            <div className="w-full">
              <div className="bg-grayBg px-1 py-2 rounded-tl-[4px] rounded-tr-[4px] border-b-[3px] border-b-linkBlue flex items-center gap-1">
                <input
                  className="w-full m-0 text-[28px] text-black bg-transparent outline-none placeholder:text-black"
                  placeholder={'כותרת צ’קליסט'}
                  autoComplete="off"
                  value={value}
                  onChange={(e) =>
                    setValue(`title`, e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                  }
                />
              </div>

              {errors.title?.message && (
                <p className="m-0 text-[12px] text-raspberryRed">
                  {errors.title?.message}
                </p>
              )}
            </div>
          )}
        />
      }
      showHeaderLine={false}
      subTitle={!watch('title') ? "שם הצ'קליסט יופיע בפאנל רשימות" : ''}
      width={924}
      fullHeight
    >
      <div className="h-full flex flex-col gap-2">
        <div
          className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto px-[32px]"
          ref={divRef}
        >
          {/* Copy Mode: Show client and branch dropdowns */}
          {copyMode && (
            <div className="flex flex-col gap-3 ">
              <p className="m-0 font-bold text-black">העתק ל:</p>

              <Controller
                name="clientId"
                control={control}
                render={({ field: { value } }) => (
                  <div className="flex items-center">
                    <div className="w-10">
                      <img
                        src={SVG.clientIcon}
                        className="w-[24px] h-[24px] object-contain"
                      />
                    </div>
                    <AppMUIAutocomplete
                      label="בית עסק"
                      size="small"
                      required
                      fullWidth
                      options={allClients || []}
                      getOptionLabel={(option) => option.name}
                      value={allClients?.find((c) => c.id === value) || null}
                      onChange={(_e, val) => {
                        setValue('clientId', val?.id || '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('branchIds', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('subBranchIds', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('departments', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('shiftTime', [], {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                    />
                  </div>
                )}
              />

              {/* <Controller
                name="branchIds"
                control={control}
                render={({ field: { value } }) => {
                  const selectedClientId = watch('clientId');
                  const clientBranches =
                    allClients?.find((c) => c.id === selectedClientId)
                      ?.branches || [];

                  return (
                    <div className="flex items-center">
                      <div className="w-10" />
                      <AppMUIAutocomplete
                        label="סניף"
                        size="small"
                        fullWidth
                        options={clientBranches}
                        getOptionLabel={(option) => option?.name}
                        disabled={!selectedClientId}
                        value={
                          clientBranches.find((b) => b.id === value?.[0]) ||
                          null
                        }
                        onChange={(_e, val) => {
                          setValue('branchIds', val?.id ? [val.id] : [], {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue('departments', [], {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                      />
                    </div>
                  );
                }}
              /> */}
            </div>
          )}

          {/* Branch Selection - full client tree at client or branch level */}
          {displayBranchTree &&
            clientForBranches &&
            clientForBranches.branches?.length > 0 && (
              <div
                className={`"w-full" ${copyMode ? 'border-b border-b-[#B9B9B9] pb-3' : ''}`}
              >
                <Controller
                  name="branchIds"
                  control={control}
                  render={({ field: { value: branchIdsValue } }) => {
                    return (
                      <div className="flex items-center">
                        {copyMode && <div className="w-10" />}
                        <BranchTreeAutocomplete
                          label="משוייך ל-"
                          showClientChip
                          allClients={[clientForBranches]}
                          value={[
                            ...(branchIdsValue || []),
                            ...(formSubBranchIds || []),
                          ]}
                          onChange={(val) => {
                            const prevBranchIds = getValues('branchIds') || [];

                            const newBranchIds =
                              val
                                ?.filter((item) => item?.isBranch)
                                ?.map((item) => item?.id) || [];
                            const newSubBranches = (val?.filter(
                              (item) => item?.isSubBranch,
                            ) || []) as SubBranchData[];

                            const removedBranchIds = prevBranchIds.filter(
                              (id) => !newBranchIds.includes(id),
                            );

                            const filteredSubBranches = newSubBranches.filter(
                              (sub) => !removedBranchIds.includes(sub.branchId),
                            );

                            const autoAddBranchIds = filteredSubBranches
                              .filter(
                                (sub) => !newBranchIds.includes(sub.branchId),
                              )
                              .map((sub) => sub.branchId);

                            const finalBranchIds = Array.from(
                              new Set([...newBranchIds, ...autoAddBranchIds]),
                            );
                            const finalSubBranchIds = filteredSubBranches.map(
                              (sub) => sub.id,
                            );

                            setValue('branchIds', finalBranchIds, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            setValue('subBranchIds', finalSubBranchIds, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            setValue('departments', [], {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            setValue('shiftTime', [], {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                        />
                      </div>
                    );
                  }}
                />
              </div>
            )}

          <div className="flex gap-3">
            <Controller
              name="departments"
              control={control}
              render={() => (
                <MUIIconCheckboxSelect
                  label="משוייך למחלקה/ות"
                  options={departmentOptions}
                  groupBy={(option) => option.groupLabel || ''}
                  size="small"
                  fullWidth
                  disableCloseOnSelect
                  id="departments-autocomplete"
                  renderValue={(selectedOptions, getTagProps) => {
                    const allSelected =
                      departments?.[0] === 'all' ||
                      selectedOptions?.length === departmentOptions.length;

                    const renderChip = (
                      option: (typeof departmentOptions)[0],
                      index = 0,
                    ) => (
                      <Chip
                        label={option.label}
                        icon={
                          <img
                            src={option.icon}
                            alt="icon"
                            className="w-[18px] h-[18px] object-contain"
                          />
                        }
                        {...getTagProps({ index })}
                      />
                    );

                    if (allSelected) {
                      const allOption = departmentOptions.find(
                        (o) => o.value === 'all',
                      );
                      return allOption ? renderChip(allOption) : null;
                    }

                    return selectedOptions.map((option, index) =>
                      renderChip(option, index),
                    );
                  }}
                  value={departmentsValue}
                  onChange={handleDepartmentsChange}
                  error={!!errors.departments}
                  helperText={errors.departments?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="shiftTime"
              render={() => (
                <AppMUIAutocomplete
                  label="משמרת"
                  size="small"
                  options={formattedShifts}
                  multiple
                  fullWidth
                  disableCloseOnSelect
                  id="shiftTime-autocomplete"
                  groupBy={(option) => option.groupLabel || ''}
                  getOptionLabel={(option) => option.label}
                  renderValue={(selectedOptions, getTagProps) => {
                    const allSelected =
                      shiftTime?.[0] === 'all' ||
                      selectedOptions?.length === formattedShifts.length;

                    const renderChip = (
                      option: (typeof formattedShifts)[0],
                      index = 0,
                    ) => (
                      <Chip label={option.label} {...getTagProps({ index })} />
                    );

                    if (allSelected) {
                      const allOption = formattedShifts.find(
                        (o) => o.value === 'all',
                      );
                      return allOption ? renderChip(allOption) : null;
                    }

                    return selectedOptions.map((option, index) =>
                      renderChip(option, index),
                    );
                  }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    const isAll = option.value === 'all';

                    return (
                      <li
                        key={key}
                        {...optionProps}
                        style={{
                          borderBottom: isAll ? '2px solid lightgray' : 'none',
                        }}
                      >
                        <Checkbox
                          size="small"
                          color="secondary"
                          checked={selected}
                        />
                        {option.label}
                      </li>
                    );
                  }}
                  value={shiftTimeValue}
                  onChange={handleShiftTimesChange}
                />
              )}
            />
          </div>

          {/* Due Time Selection */}
          {watch(SWITCHES[1].name) && shiftTime?.length > 0 && (
            <Controller
              control={control}
              name="dueNotificationShiftOffset"
              render={({ field: { value } }) => (
                <AppMUIAutocomplete
                  label="זמן יעד לסיום"
                  size="small"
                  options={DUE_NOTIFICATION_SHIFT_OFFSET_OPTIONS}
                  fullWidth
                  id="dueNotificationShiftOffset-autocomplete"
                  getOptionLabel={(option) => option.label}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;

                    return (
                      <li key={key} {...optionProps}>
                        {option.label}
                      </li>
                    );
                  }}
                  value={
                    DUE_NOTIFICATION_SHIFT_OFFSET_OPTIONS.find(
                      (option) => option.value === value,
                    ) || null
                  }
                  onChange={(_, newValue) =>
                    setValue(
                      'dueNotificationShiftOffset',
                      (
                        newValue as
                          | (typeof DUE_NOTIFICATION_SHIFT_OFFSET_OPTIONS)[0]
                          | null
                      )?.value ?? '',
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      },
                    )
                  }
                  error={!!errors.dueNotificationShiftOffset}
                  helperText={errors.dueNotificationShiftOffset?.message}
                />
              )}
            />
          )}

          {watch(SWITCHES[1].name) && shiftTime?.length <= 0 && (
            <div className="flex gap-3">
              <div className="flex items-center gap-2 w-full">
                <img
                  src={SVG.clockIcon}
                  alt={"clock"}
                  className="w-[20px] h-[20px] object-contain"
                />
                <Controller
                  control={control}
                  name="dueNotificationTime"
                  render={({ field }) => (
                    <AppMUIAutocomplete
                      freeSolo
                      disableClearable
                      options={TIMES.map((item) => item.title)}
                      label="זמן יעד לסיום"
                      size="small"
                      fullWidth
                      value={field.value || ''}
                      onChange={(_, value) =>
                        setValue('dueNotificationTime', value || '', {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        })
                      }
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') {
                          setValue('dueNotificationTime', value, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                        }
                      }}
                      error={!!errors.dueNotificationTime}
                      helperText={errors.dueNotificationTime?.message}
                    />
                  )}
                />
              </div>

              <Controller
                control={control}
                name="dueNotificationDate"
                render={({ field }) => (
                  <DateInput
                    dateFormat="EEEE, d 'ב'MMMM"
                    // placeholderText={new Date()
                    //   .toLocaleDateString('he-IL', {
                    //     weekday: 'long',
                    //     day: 'numeric',
                    //     month: 'long',
                    //   })
                    //   .replace(/^יום\s*/, '')}
                    selected={field.value ? moment(field.value).toDate() : null}
                    onChange={(date) =>
                      setValue(
                        'dueNotificationDate',
                        date ? moment(date).format('YYYY-MM-DD') : '',
                        {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        },
                      )
                    }
                    label="ביום-"
                  />
                )}
              />
            </div>
          )}

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <AppTextInput
                type="text"
                autoComplete="off"
                autoCorrect="off"
                placeholder="הוספת תיאור"
                {...field}
                error={errors.description?.message || ''}
                startIcon={
                  <img
                    src={SVG.descriptionDarkIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                }
              />
            )}
          />

          <div className="flex extraSmall:flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-2 border-b-2 border-b-tealBlue">
            <p className="m-0 text-[16px] font-semibold text-black">
              {checklistHeaderText}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              {SWITCHES.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <Controller
                    name={item.name}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <AppSwitch onChange={onChange} checked={value} />
                    )}
                  />
                  <p
                    className={`m-0 text-[18px] text-darkerGray ${watch(item.name) ? 'font-medium' : ''}`}
                  >
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {listFields.map((item, index) => {
            const srNo = (index + 1).toString().padStart(2, '0');
            const shouldAutoFocus = newlyAddedIndex === index;
            return (
              <ChecklistBox
                key={item.id}
                index={index}
                srNo={srNo}
                control={control}
                OnRemove={listRemove}
                setValue={setValue}
                autoFocus={shouldAutoFocus}
              />
            );
          })}

          <div className="flex flex-col gap-1">
            {errors?.list || errors?.list?.length ? (
              <p className="m-0 text-[12px] text-raspberryRed">
                נדרש פריט ברשימה עם טקסט!
              </p>
            ) : null}

            {listFields?.length == 0 && (
              <div className="flex pb-2 border-b border-b-lightDarkGray">
                <button
                  className="outline-none flex items-center gap-2 px-1 hover:opacity-80 transition-opacity duration-200"
                  onClick={addToCheckList}
                >
                  <div className="w-[14px] h-[14px]">
                    <img
                      src={SVG.addIcon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="m-0 text-[20px] font-medium text-gray">
                    הוספה לרשימה
                  </p>
                </button>
              </div>
            )}

            <div className="flex pb-2 border-b border-b-lightDarkGray">
              <button
                className="outline-none flex items-center gap-2 px-1 hover:opacity-80 transition-opacity duration-200"
                onClick={addToCheckList}
              >
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.addIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="m-0 text-[20px] font-medium text-gray">
                  הוספה לרשימה
                </p>
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end px-[32px]">
          <div className="flex items-center gap-2">
            <AppButton onClick={handleModalClose}>ביטול</AppButton>

            <AppButton
              variant="primary"
              disabled={!isValid}
              isLoading={isSubmitting || isPending || isUpdatePending}
              onClick={handleSubmit(onSubmit)}
            >
              {copyMode ? 'העתקה' : 'שמירה'}
            </AppButton>
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default AddEditCheckList;
